<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useBaseStore } from "@/stores/base";
import { checkBackendReachability } from "@/api/helpers/backendReachability";

const AUTO_RETRY_INTERVAL_MS = 10000;

const baseStore = useBaseStore();
const isRetrying = ref(false);
let autoRetryInterval: ReturnType<typeof setInterval> | null = null;

const isUnreachable = computed(() => baseStore.isBackendUnreachable);

const retry = async () => {
	if (isRetrying.value) {
		return;
	}

	isRetrying.value = true;
	await checkBackendReachability();
	isRetrying.value = false;
};

const startAutoRetry = () => {
	if (autoRetryInterval) {
		return;
	}
	autoRetryInterval = setInterval(retry, AUTO_RETRY_INTERVAL_MS);
};

const stopAutoRetry = () => {
	if (autoRetryInterval) {
		clearInterval(autoRetryInterval);
		autoRetryInterval = null;
	}
};

watch(
	isUnreachable,
	(unreachable) => (unreachable ? startAutoRetry() : stopAutoRetry()),
	{ immediate: true }
);

const onOnline = () => {
	if (isUnreachable.value) {
		retry();
	}
};

onMounted(() => window.addEventListener("online", onOnline));
onBeforeUnmount(() => {
	window.removeEventListener("online", onOnline);
	stopAutoRetry();
});
</script>

<template>
	<v-overlay
		:model-value="isUnreachable"
		persistent
		class="align-center justify-center"
		z-index="3000"
	>
		<v-card
			class="help"
			prepend-icon="fa: fa-duotone fa-tower-broadcast"
			title="Unable to reach the server"
			variant="flat"
			max-width="600"
		>
			<v-divider />
			<div class="pa-5 pb-0">
				<div class="pb-2">
					Your computer is currently unable to connect to our server. This can
					happen for several reasons, such as an unstable internet connection or
					network restrictions.
				</div>
				<div class="pb-4">
					Access to this environment may be limited to approved networks. If
					you're working remotely, connecting to your company's VPN might
					resolve the issue.
				</div>
				<div class="pb-4">
					If your internet connection and network access are working correctly,
					the issue may be on our side. In that case, we're likely already aware
					of it and working to restore connectivity.
				</div>
			</div>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn variant="flat" :loading="isRetrying" @click="retry">
					Retry now
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-overlay>
</template>

<style scoped lang="scss"></style>
